import { createBrowserRouter, Navigate, useRouteError } from 'react-router-dom';
import { RootOutlet } from './RootOutlet';
import React from 'react';
import ErrorAlert from './app/modules/dashboard/components/alerts/ErrorAlert';
import { SafeSuspense } from './app/components/error-boundary/SafeSuspense';
import { FullPageLoadingSpinner } from './app/components/loading/FullPageLoadingSpinner';

// --------------------- PUBLIC ---------------------

const AsyncPublicRouterOutlet = React.lazy(
	async () =>
		await import('./app/modules/public/pages/router/PublicRouterOutlet')
);

const AsyncPolicyCertificate = React.lazy(
	async () =>
		await import(
			'./app/modules/public/pages/policies/policy-certificate/PolicyCertificate'
		)
);

// --------------------- END PUBLIC ---------------------

// --------------------- AUTH ---------------------
const AsyncRouteOutletAuth = React.lazy(
	async () => await import('./app/modules/auth/pages/router/AuthRootOutlet')
);

const AsyncAuthRedirectPage = React.lazy(
	async () =>
		await import('./app/modules/auth/pages/auth-redirect/AuthRedirect')
);

const AsyncLogoutPage = React.lazy(
	async () => await import('./app/modules/auth/pages/logout/LogoutPage')
);

const AsyncLoginPage = React.lazy(
	async () => await import('./app/modules/auth/pages/login/LoginPage')
);

const AsyncSignUpPage = React.lazy(
	async () => await import('./app/modules/auth/pages/sign-up/SignUpPage')
);

const AsyncSuccessPageEmailConfirmationPage = React.lazy(
	async () =>
		await import('./app/modules/auth/pages/success-pages/EmailConfirmationPage')
);

const AsyncErrorAuthPage = React.lazy(
	async () => await import('./app/modules/auth/pages/error/ErrorAuthPage')
);

const AsyncAccountNotFoundPage = React.lazy(
	async () => await import('./app/modules/auth/pages/404/AccountNotFound')
);
const AsyncAccountNotActivatePage = React.lazy(
	async () => await import('./app/modules/auth/pages/404/AccountNotActive')
);

const AsyncForceResetPasswordPage = React.lazy(
	async () =>
		await import(
			'./app/modules/auth/pages/forced-reset-password/ForcedToResetPassword'
		)
);

const AsyncRegisterUserPage = React.lazy(
	async () => await import('./app/modules/auth/pages/register/RegisterUser')
);

// --------------------- END AUTH ---------------------

// --------------------- PUBLIC ONBOARDING ---------------------
const AsyncPublicOnBoardingRootOutletRootOutlet = React.lazy(
	async () =>
		await import(
			'./app/modules/onboarding/pages/router/PublicOnBoardingRootOutlet'
		)
);

const AsyncSelectUserTypePage = React.lazy(
	async () =>
		await import(
			'./app/modules/onboarding/pages/select-user-type/SelectUserTypePage'
		)
);
// --------------------- END PUBLIC ONBOARDING ---------------------

// --------------------- IPAF ---------------------

const AsyncIpafRootOutlet = React.lazy(
	async () => await import('./app/modules/ipaf/pages/router/IpafRouter')
);

const AsyncOnePageIpaf = React.lazy(
	async () => await import('./app/modules/ipaf/pages/one-page/OnePage')
);

const AsyncOnePagePreIpaf = React.lazy(
	async () => await import('./app/modules/ipaf/pages/preipaf/OnePagePreIpaf')
);

const AsyncIpafSuccessPage = React.lazy(
	async () =>
		await import('./app/modules/onboarding/pages/ipaf-success/IpafSuccesPage')
);

// --------------------- END IPAF ---------------------

// --------------------- PRE APP ---------------------
const AsyncPreApplicationPage = React.lazy(
	async () =>
		await import(
			'./app/modules/onboarding/pages/pre-application/PreApplicationPage'
		)
);
// --------------------- END PRE APP ---------------------

// --------------------- DASHBOARD ---------------------
const AsyncDashboardRootOutlet = React.lazy(
	async () =>
		await import('./app/modules/dashboard/pages/router/DashboardRootOutlet')
);

const AsyncDashboardHomePage = React.lazy(
	async () => await import('./app/modules/dashboard/pages/home/NewHome')
);

const AsyncBrokerCustomerHome = React.lazy(
	async () =>
		await import('./app/modules/dashboard/pages/home/BrokerCustomerHome')
);

const AsyncDashboardCoveragePage = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/coverages/DashboardCoveragePage'
		)
);

const AsyncDashboardQuotesRootOutletRootOutlet = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/router/DashboardQuotesRootOutlet'
		)
);

const AsyncDashboardQuotesPage = React.lazy(
	async () =>
		await import('./app/modules/dashboard/pages/quotes/DashboardQuotesHomePage')
);

const AsyncDashboardQuotesDetailPage = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/quotes/DashboardQuotesDetailPage'
		)
);

const AsyncDashboardQuotesMoreDetailPage = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/quotes/DashboardQuotesMoreDetail'
		)
);

const AsyncDashboardPoliciesRootOutletRootOutlet = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/router/DashboardPoliciesRootOutlet'
		)
);

const AsyncDashboardPoliciesPage = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/policies/DashboardPolicyHomePage'
		)
);

const AsyncDashboardPolicyDetailPage = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/policies/DashboardPolicyDetailPage'
		)
);

const AsyncDashboardPolicyMoreDetailPage = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/policies/DashboardPolicyMoreDetail'
		)
);

const AsyncDashboardClaimsRootOutletRootOutlet = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/router/DashboardClaimsRootOutlet'
		)
);

const AsyncDashboardClaimsPage = React.lazy(
	async () =>
		await import('./app/modules/dashboard/pages/claims/ClaimsHomaPage')
);

const AsyncDashboardClaimsDetailPage = React.lazy(
	async () =>
		await import('./app/modules/dashboard/pages/claims/ClaimsDetailPage')
);

const AsyncDashboardCreateClaim = React.lazy(
	async () =>
		await import('./app/modules/dashboard/pages/claims/CreateClaimPage')
);

const AsyncDashboardCyberRootOutlet = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/router/DashboardCyberRootOutlet'
		)
);

const AsyncDashboardCyberHome = React.lazy(
	async () =>
		await import('./app/modules/dashboard/pages/cyber/DashboardCyberHomePage')
);

const AsyncDashboardCyberCreateAssessment = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/cyber/DashboardCyberCreateAssessmentPage'
		)
);

const AsyncDashboardCyberSuccessScreen = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/cyber/DashboardCyberSuccessScreen'
		)
);

const AsyncUsersRootOutletRootOutlet = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/router/DashboardUsersRootOutlet'
		)
);

const AsyncDashboardListUsers = React.lazy(
	async () =>
		await import('./app/modules/dashboard/pages/users/DashboardListUsers')
);

const AsyncDashboardScheduleMeetingPage = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/schedule-meeting/ScheduleMeetingPage'
		)
);

const AsyncDashboardProcessRedirectPage = React.lazy(
	async () =>
		await import(
			'./app/modules/dashboard/pages/process-redirect/ProcessRedirect'
		)
);

const AsyncRedirectInformationRootOutletRootOutlet = React.lazy(
	async () =>
		await import(
			'./app/modules/onboarding/pages/router/RedirectInformationOutlet'
		)
);

const AsyncSuccessPage = React.lazy(
	async () =>
		await import('./app/modules/onboarding/pages/success-page/SuccessPage')
);

// --------------------- END DASHBOARD ---------------------

// --------------------- RENEW ---------------------

const AsyncRenewRootOutletRootOutlet = React.lazy(
	async () =>
		await import(
			'./app/modules/questions-confirmation/pages/router/RenewRouterOutlet'
		)
);

const AsyncRenewPage = React.lazy(
	async () =>
		await import('./app/modules/questions-confirmation/pages/renew/RenewPage')
);

// --------------------- END RENEW ---------------------

// --------------------- BROKER SIGNUP ---------------------

const AsyncBrokerSignup = React.lazy(
	async () => await import('./app/modules/broker/pages/signup/BrokerSignup')
);

const AsyncBrokerSignupForm = React.lazy(
	async () =>
		await import('./app/modules/broker/pages/signup-form/BrokerSignupForm')
);

const AsyncBrokerSignupFinish = React.lazy(
	async () =>
		await import('./app/modules/broker/pages/signup-finish/BrokerSignupFinish')
);

// --------------------- END BROKER SIGNUP ---------------------

// --------------------- BROKER REGISTER ---------------------

const AsyncBrokerRegister = React.lazy(
	async () => await import('./app/modules/broker/pages/register/BrokerRegister')
);

const AsyncBrokerRegisterForm = React.lazy(
	async () =>
		await import('./app/modules/broker/pages/register-form/BrokerRegisterForm')
);

// --------------------- END BROKER REGISTER ---------------------

// --------------------- DASHBOARD BROKERS ---------------------

const AsyncBrokerHome = React.lazy(
	async () => await import('./app/modules/broker/pages/home/BrokerHomeV3')
);

const AsyncBrokerAppRouterOutlet = React.lazy(
	async () =>
		await import('./app/modules/broker/pages/router/BrokerAppRouterOutlet')
);

const AsyncBrokerQuotesRouterOutlet = React.lazy(
	async () =>
		await import('./app/modules/broker/pages/router/BrokerQuoteRouterOutlet')
);

const AsyncQuoteFullPage = React.lazy(
	async () =>
		await import(
			'./app/modules/broker/pages/quote-full-page/pages/QuoteFullPage'
		)
);

const AsyncPolicyFullPage = React.lazy(
	async () =>
		await import('./app/modules/broker/pages/policy-full-page/PolicyFullPage')
);

const AsyncQuoteCheckoutFullPage = React.lazy(
	async () =>
		await import(
			'./app/modules/broker/pages/quote-full-page/pages/QuoteCheckoutFullPage'
		)
);

const AsyncEarningRootOutlet = React.lazy(
	async () =>
		await import('./app/modules/broker/pages/router/EarningRootOutlet')
);

const AsyncEarningHomePage = React.lazy(
	async () =>
		await import('./app/modules/broker/pages/earnings/EarningHomePage')
);

const AsyncSendFiscalNote = React.lazy(
	async () => await import('./app/modules/broker/pages/earnings/SendFiscalNote')
);

const AsyncListFiscalNote = React.lazy(
	async () => await import('./app/modules/broker/pages/earnings/ListFiscalNote')
);

const AsyncKamBroker = React.lazy(
	async () => await import('./app/modules/broker/pages/kam-broker/KamBroker')
);

const AsyncCompanyRootOutlet = React.lazy(
	async () => await import('./app/modules/broker/pages/router/CompanyRoot')
);

const AsyncBrokerCompanyDetail = React.lazy(
	async () =>
		await import(
			'./app/modules/broker/pages/broker-company-detail/BrokerCompanyDetailV3'
		)
);

const AsyncBrokerQuotes = React.lazy(
	async () => await import('./app/modules/broker/pages/quotes/BrokerQuotes')
);

const AsyncBrokerQuoteDetail = React.lazy(
	async () =>
		await import('./app/modules/broker/pages/quote-detail/BrokerQuoteDetail')
);

const AsyncBrokerQuoteMoreDetail = React.lazy(
	async () =>
		await import(
			'./app/modules/broker/pages/quote-detail/BrokerQuoteMoreDetail'
		)
);

const AsyncBrokerQuoteCheckout = React.lazy(
	async () =>
		await import(
			'./app/modules/broker/pages/quote-checkout/BrokerQuoteCheckoutV2'
		)
);

const AsyncBrokerPolicyDetail = React.lazy(
	async () =>
		await import('./app/modules/broker/pages/policy-detail/BrokerPolicyDetail')
);

const AsyncBrokerPolicyMoreDetail = React.lazy(
	async () =>
		await import(
			'./app/modules/broker/pages/policy-detail/BrokerPolicyMoreDetail'
		)
);

const AsyncBrokerBankDetails = React.lazy(
	async () =>
		await import('./app/modules/broker/pages/bank-details/BrokerBankDetails')
);

// --------------------- END DASHBOARD BROKERS ---------------------

// --------------------- EXAMPLE FORMS ---------------------

const AsyncExampleFormsRootOutlet = React.lazy(
	async () => await import('./app/modules/forms-v2/pages/FormsExamples')
);
// --------------------- END EXAMPLE FORMS ---------------------

const AsyncBrokerSuccessPage = React.lazy(
	async () =>
		await import(
			'./app/modules/onboarding/pages/broker-success-page/BrokerSuccessPage'
		)
);

const AsyncIpafRejectionPage = React.lazy(
	async () =>
		await import(
			'./app/modules/onboarding/pages/ipaf-rejected/IpafRejectedPage'
		)
);

const AsyncQuestionInformationRootOutletRootOutlet = React.lazy(
	async () =>
		await import(
			'./app/modules/questions-confirmation/pages/router/QuestionInformationRouterOutlet'
		)
);

const AsyncStepInfoPage = React.lazy(
	async () =>
		await import(
			'./app/modules/questions-confirmation/pages/step-info/StepInfoPage'
		)
);

// --------------------- SHARE IPAF ---------------------

const AsyncShareIpafRouter = React.lazy(
	async () => await import('./app/modules/ipaf/pages/router/ShareIpafRouter')
);

const AsyncPageShareIpaf = React.lazy(
	async () => await import('./app/modules/ipaf/pages/share-ipaf/ShareIpaf')
);

const AsyncPageIpafNotFound = React.lazy(
	async () =>
		await import('./app/modules/ipaf/pages/ipaf-not-found/IpafNotFound')
);

const AsyncPublicIpafRootOutlet = React.lazy(
	async () => await import('./app/modules/ipaf/pages/router/PublicIpafRouter')
);

const AsyncPublicOnePageIpaf = React.lazy(
	async () =>
		await import('./app/modules/ipaf/pages/public-one-page/PublicOnePageIpaf')
);

const AsyncIpafEnding = React.lazy(
	async () => await import('./app/modules/ipaf/pages/ipaf-ending/IpafEnding')
);

// --------------------- END SHARE IPAF ---------------------

function ErrorBoundary(): any {
	const error = useRouteError();
	console.error(error);
	return (
		<>
			<ErrorAlert message={'Há um erro inesperado'} />
		</>
	);
}

export const ROUTER = createBrowserRouter([
	{
		path: '*',
		element: <Navigate to='/public/404' replace />,
	},
	{
		path: 'public',
		errorElement: <ErrorBoundary />,
		element: (
			<SafeSuspense
				fallback={
					<FullPageLoadingSpinner message='Loading public content...' />
				}
			>
				<AsyncPublicRouterOutlet />
			</SafeSuspense>
		),
		children: [
			{
				path: 'policy',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading policy certificate...' />
						}
					>
						<AsyncPolicyCertificate title='TAB_TITLE' />
					</SafeSuspense>
				),
			},
			{
				path: '404',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={<FullPageLoadingSpinner message='Not found' />}
					>
						<div>página não encontrada</div>
					</SafeSuspense>
				),
			},
		],
	},
	{
		path: 'auth',
		element: (
			<SafeSuspense
				fallback={
					<FullPageLoadingSpinner message='Loading authentication...' />
				}
			>
				<AsyncRouteOutletAuth />
			</SafeSuspense>
		),
		children: [
			{
				path: 'auth-redirect',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={<FullPageLoadingSpinner message='Redirecting...' />}
					>
						<AsyncAuthRedirectPage title='TAB_TITLE' />
					</SafeSuspense>
				),
			},
			{
				path: 'logout',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={<FullPageLoadingSpinner message='Logging out...' />}
					>
						<AsyncLogoutPage title='TAB_TITLE' />
					</SafeSuspense>
				),
			},
			{
				path: 'login',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading login page...' />
						}
					>
						<AsyncLoginPage title='TAB_TITLE' />
					</SafeSuspense>
				),
			},
			{
				path: 'sign-up',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading sign-up page...' />
						}
					>
						<AsyncSignUpPage title='TAB_TITLE' />
					</SafeSuspense>
				),
			},
			{
				path: 'email-confirmation',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading confirmation page...' />
						}
					>
						<AsyncSuccessPageEmailConfirmationPage title='EMAIL_AUTH_CONFIRMATION' />
					</SafeSuspense>
				),
			},
			{
				path: 'error',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading error page...' />
						}
					>
						<AsyncErrorAuthPage title='EMAIL_AUTH_CONFIRMATION' />
					</SafeSuspense>
				),
			},
			{
				path: 'account-not-found',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading account not found page...' />
						}
					>
						<AsyncAccountNotFoundPage title='EMAIL_AUTH_CONFIRMATION' />
					</SafeSuspense>
				),
			},
			{
				path: 'account-not-active',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading account not active page...' />
						}
					>
						<AsyncAccountNotActivatePage title='EMAIL_AUTH_CONFIRMATION' />
					</SafeSuspense>
				),
			},
			{
				path: 'force-restore-password',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading password reset page...' />
						}
					>
						<AsyncForceResetPasswordPage title='EMAIL_AUTH_CONFIRMATION' />
					</SafeSuspense>
				),
			},
			{
				path: 'register',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading registration page...' />
						}
					>
						<AsyncRegisterUserPage title='TAB_TITLE' />
					</SafeSuspense>
				),
			},
		],
	},
	{
		path: ':country/:language',
		element: <RootOutlet />,
		children: [
			{
				path: 'ipaf-ending',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading IPAF ending page...' />
						}
					>
						<AsyncIpafEnding title='TAB_TITLE' />
					</SafeSuspense>
				),
			},
			{
				path: 'question-information',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading question information...' />
						}
					>
						<AsyncQuestionInformationRootOutletRootOutlet />
					</SafeSuspense>
				),
				children: [
					{
						path: 'step-info/:step',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading step information...' />
								}
							>
								<AsyncStepInfoPage title='IPAF_TAB_TITLE' />
							</SafeSuspense>
						),
					},
				],
			},
			{
				path: 'forms-demo',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading forms demo...' />
						}
					>
						<AsyncExampleFormsRootOutlet />
					</SafeSuspense>
				),
			},
			{
				path: 'broker-success-screen/:type',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading broker success page...' />
						}
					>
						<AsyncBrokerSuccessPage title='TAB_TITLE' />
					</SafeSuspense>
				),
			},
			{
				path: 'user-success-screen/:type',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading user success page...' />
						}
					>
						<AsyncBrokerSuccessPage title='TAB_TITLE' />
					</SafeSuspense>
				),
			},
			{
				path: 'ipaf-rejected',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading IPAF rejection page...' />
						}
					>
						<AsyncIpafRejectionPage title='TAB_TITLE' />
					</SafeSuspense>
				),
			},
			{
				path: 'public-onboarding',
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading onboarding...' />
						}
					>
						<AsyncPublicOnBoardingRootOutletRootOutlet />
					</SafeSuspense>
				),
				children: [
					{
						path: 'select-user-type',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading user type selection...' />
								}
							>
								<AsyncSelectUserTypePage title='PRE_APPLICATION_TAB_TITLE' />
							</SafeSuspense>
						),
					},
				],
			},
			{
				path: 'ipaf',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={<FullPageLoadingSpinner message='Loading IPAF...' />}
					>
						<AsyncIpafRootOutlet />
					</SafeSuspense>
				),
				children: [
					{
						path: 'one-page',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading IPAF form...' />
								}
							>
								<AsyncOnePageIpaf />
							</SafeSuspense>
						),
					},
					{
						path: 'prev',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading pre-IPAF form...' />
								}
							>
								<AsyncOnePagePreIpaf />
							</SafeSuspense>
						),
					},
					{
						path: 'success',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading success page...' />
								}
							>
								<AsyncIpafSuccessPage title='IPAF_SUCCESS_TITLE' />
							</SafeSuspense>
						),
					},
				],
			},
			{
				path: 'public-ipaf',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading public IPAF...' />
						}
					>
						<AsyncPublicIpafRootOutlet />
					</SafeSuspense>
				),
				children: [
					{
						path: 'one-page',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading public IPAF form...' />
								}
							>
								<AsyncPublicOnePageIpaf />
							</SafeSuspense>
						),
					},
					{
						path: 'success',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading success page...' />
								}
							>
								<AsyncIpafSuccessPage title='IPAF_SUCCESS_TITLE' />
							</SafeSuspense>
						),
					},
				],
			},
			{
				path: 'shared-ipaf',
				errorElement: <ErrorBoundary />,
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading shared IPAF...' />
						}
					>
						<AsyncShareIpafRouter />
					</SafeSuspense>
				),
				children: [
					{
						path: 'index',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading shared IPAF page...' />
								}
							>
								<AsyncPageShareIpaf />
							</SafeSuspense>
						),
					},
					{
						path: 'ipaf-not-found',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading IPAF not found page...' />
								}
							>
								<AsyncPageIpafNotFound />
							</SafeSuspense>
						),
					},
				],
			},
			{
				path: 'dashboard',
				element: (
					<SafeSuspense
						fallback={<FullPageLoadingSpinner message='Loading dashboard...' />}
					>
						<AsyncDashboardRootOutlet />
					</SafeSuspense>
				),
				children: [
					{
						index: true,
						path: 'index',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading dashboard home...' />
								}
							>
								<AsyncDashboardHomePage title='TAB_TITLE' />
							</SafeSuspense>
						),
					},
					{
						path: 'index-customer',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading customer home...' />
								}
							>
								<AsyncBrokerCustomerHome />
							</SafeSuspense>
						),
					},
					{
						index: true,
						path: 'coverages',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading coverages...' />
								}
							>
								<AsyncDashboardCoveragePage title='TAB_TITLE' />
							</SafeSuspense>
						),
					},
					{
						path: 'quotes',
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading quotes section...' />
								}
							>
								<AsyncDashboardQuotesRootOutletRootOutlet />
							</SafeSuspense>
						),
						children: [
							{
								index: true,
								path: 'home',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading quotes home...' />
										}
									>
										<AsyncDashboardQuotesPage title='TAB_TITLE' />
									</SafeSuspense>
								),
							},
							{
								index: true,
								path: ':type/:product/:userFormId',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading quote details...' />
										}
									>
										<AsyncDashboardQuotesDetailPage title='TAB_TITLE' />
									</SafeSuspense>
								),
							},
							{
								index: true,
								path: 'detail/:type/:product/:userFormId',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading quote more details...' />
										}
									>
										<AsyncDashboardQuotesMoreDetailPage title='TAB_TITLE' />
									</SafeSuspense>
								),
							},
						],
					},
					{
						path: 'policies',
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading policies section...' />
								}
							>
								<AsyncDashboardPoliciesRootOutletRootOutlet />
							</SafeSuspense>
						),
						children: [
							{
								index: true,
								path: 'home',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading policies home...' />
										}
									>
										<AsyncDashboardPoliciesPage title='TAB_TITLE' />
									</SafeSuspense>
								),
							},
							{
								index: true,
								path: 'detail/:id',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading policy details...' />
										}
									>
										<AsyncDashboardPolicyDetailPage title='TAB_TITLE' />
									</SafeSuspense>
								),
							},
							{
								index: true,
								path: 'detail/:id/more-info',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading policy more details...' />
										}
									>
										<AsyncDashboardPolicyMoreDetailPage title='TAB_TITLE' />
									</SafeSuspense>
								),
							},
						],
					},
					{
						path: 'claims',
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading claims section...' />
								}
							>
								<AsyncDashboardClaimsRootOutletRootOutlet />
							</SafeSuspense>
						),
						children: [
							{
								index: true,
								path: 'home',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading claims home...' />
										}
									>
										<AsyncDashboardClaimsPage title='TAB_TITLE' />
									</SafeSuspense>
								),
							},
							{
								index: true,
								path: 'detail/:id',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading claim details...' />
										}
									>
										<AsyncDashboardClaimsDetailPage title='TAB_TITLE' />
									</SafeSuspense>
								),
							},
							{
								index: true,
								path: 'create',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading claim creation form...' />
										}
									>
										<AsyncDashboardCreateClaim title='TAB_TITLE' />
									</SafeSuspense>
								),
							},
						],
					},
					{
						path: 'cyber',
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading cyber section...' />
								}
							>
								<AsyncDashboardCyberRootOutlet />
							</SafeSuspense>
						),
						children: [
							{
								index: true,
								path: 'home',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading cyber home...' />
										}
									>
										<AsyncDashboardCyberHome title='TAB_TITLE' />
									</SafeSuspense>
								),
							},
							{
								index: true,
								path: 'create',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading cyber assessment creation...' />
										}
									>
										<AsyncDashboardCyberCreateAssessment title='TAB_TITLE' />
									</SafeSuspense>
								),
							},
							{
								path: 'success-screen',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading success screen...' />
										}
									>
										<AsyncDashboardCyberSuccessScreen title='TAB_TITLE' />
									</SafeSuspense>
								),
							},
						],
					},
					{
						path: 'users',
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading users section...' />
								}
							>
								<AsyncUsersRootOutletRootOutlet />
							</SafeSuspense>
						),
						children: [
							{
								index: true,
								path: 'list',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading users list...' />
										}
									>
										<AsyncDashboardListUsers />
									</SafeSuspense>
								),
							},
						],
					},
					{
						path: 'schedule-meeting',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading meeting scheduler...' />
								}
							>
								<AsyncDashboardScheduleMeetingPage title='TAB_TITLE' />
							</SafeSuspense>
						),
					},
					{
						path: 'process-redirect',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Processing redirect...' />
								}
							>
								<AsyncDashboardProcessRedirectPage title='TAB_TITLE' />
							</SafeSuspense>
						),
					},
				],
			},
			{
				path: 'redirect-information',
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading redirect information...' />
						}
					>
						<AsyncRedirectInformationRootOutletRootOutlet />
					</SafeSuspense>
				),
				children: [
					{
						path: 'success/:category',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading success page...' />
								}
							>
								<AsyncSuccessPage title='IPAF_TAB_TITLE' />
							</SafeSuspense>
						),
					},
				],
			},
			{
				path: 'flows',
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading flows section...' />
						}
					>
						<AsyncRenewRootOutletRootOutlet />
					</SafeSuspense>
				),
				children: [
					{
						path: 'renewal',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading renewal page...' />
								}
							>
								<AsyncRenewPage title='IPAF_TAB_TITLE' />
							</SafeSuspense>
						),
					},
				],
			},
			{
				path: 'broker-signup',
				children: [
					{
						index: true,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading broker signup...' />
								}
							>
								<AsyncBrokerSignup />
							</SafeSuspense>
						),
					},
					{
						path: 'step/:stepId',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading signup form...' />
								}
							>
								<AsyncBrokerSignupForm />
							</SafeSuspense>
						),
					},
					{
						path: 'finish',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading signup completion...' />
								}
							>
								<AsyncBrokerSignupFinish />
							</SafeSuspense>
						),
					},
				],
			},
			{
				path: 'broker',
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading broker section...' />
						}
					>
						<AsyncBrokerAppRouterOutlet />
					</SafeSuspense>
				),
				children: [
					{
						path: 'index',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading broker home...' />
								}
							>
								<AsyncBrokerHome />
							</SafeSuspense>
						),
					},
					{
						path: 'earnings',
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading earnings section...' />
								}
							>
								<AsyncEarningRootOutlet />
							</SafeSuspense>
						),
						children: [
							{
								path: 'home',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading earnings home...' />
										}
									>
										<AsyncEarningHomePage />
									</SafeSuspense>
								),
							},
							{
								path: 'send-fiscal-note',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading fiscal note form...' />
										}
									>
										<AsyncSendFiscalNote />
									</SafeSuspense>
								),
							},
							{
								path: 'list-fiscal-note',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading fiscal notes list...' />
										}
									>
										<AsyncListFiscalNote />
									</SafeSuspense>
								),
							},
						],
					},
					{
						path: 'kam/:kamId',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading KAM details...' />
								}
							>
								<AsyncKamBroker />
							</SafeSuspense>
						),
					},
					{
						path: 'company/:id',
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading company details...' />
								}
							>
								<AsyncCompanyRootOutlet />
							</SafeSuspense>
						),
						children: [
							{
								path: 'detail',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading company detail view...' />
										}
									>
										<AsyncBrokerCompanyDetail />
									</SafeSuspense>
								),
							},
							{
								path: 'quotes',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading company quotes...' />
										}
									>
										<AsyncBrokerQuotes />
									</SafeSuspense>
								),
							},
							{
								path: 'quotes/:quoteId/detail',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading quote details...' />
										}
									>
										<AsyncBrokerQuoteDetail />
									</SafeSuspense>
								),
							},
							{
								path: 'quotes/:quoteId/more-detail',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading quote more details...' />
										}
									>
										<AsyncBrokerQuoteMoreDetail />
									</SafeSuspense>
								),
							},
							{
								path: 'quotes/:quoteId/checkout',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading quote checkout...' />
										}
									>
										<AsyncBrokerQuoteCheckout />
									</SafeSuspense>
								),
							},
							{
								path: 'policies/:policyId/detail',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading policy details...' />
										}
									>
										<AsyncBrokerPolicyDetail />
									</SafeSuspense>
								),
							},
							{
								path: 'policies/:policyId/more-detail',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading policy more details...' />
										}
									>
										<AsyncBrokerPolicyMoreDetail />
									</SafeSuspense>
								),
							},
						],
					},
					{
						path: 'users',
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading users section...' />
								}
							>
								<AsyncUsersRootOutletRootOutlet />
							</SafeSuspense>
						),
						children: [
							{
								index: true,
								path: 'list',
								errorElement: <ErrorBoundary />,
								element: (
									<SafeSuspense
										fallback={
											<FullPageLoadingSpinner message='Loading users list...' />
										}
									>
										<AsyncDashboardListUsers />
									</SafeSuspense>
								),
							},
						],
					},
					{
						path: 'edit-profile',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading profile editor...' />
								}
							>
								<AsyncBrokerBankDetails />
							</SafeSuspense>
						),
					},
				],
			},
			{
				path: 'broker-quote',
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading quotes section...' />
						}
					>
						<AsyncBrokerQuotesRouterOutlet />
					</SafeSuspense>
				),
				children: [
					{
						path: 'detail',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading quote details...' />
								}
							>
								<AsyncQuoteFullPage />
							</SafeSuspense>
						),
					},
					{
						path: 'checkout',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading quote checkout...' />
								}
							>
								<AsyncQuoteCheckoutFullPage />
							</SafeSuspense>
						),
					},
				],
			},
			{
				path: 'broker-policy',
				element: (
					<SafeSuspense
						fallback={
							<FullPageLoadingSpinner message='Loading policy section...' />
						}
					>
						<AsyncBrokerQuotesRouterOutlet />
					</SafeSuspense>
				),
				children: [
					{
						path: 'detail',
						errorElement: <ErrorBoundary />,
						element: (
							<SafeSuspense
								fallback={
									<FullPageLoadingSpinner message='Loading policy details...' />
								}
							>
								<AsyncPolicyFullPage />
							</SafeSuspense>
						),
					},
				],
			},
		],
	},
]);
